import { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import type { PrepChecklistItem } from "@tih/shared";
import { useSiteCopy } from "../../context/SiteCopyContext";
import { getAtPath } from "../../lib/siteCopyPaths";
import { loadPrepProgress, savePrepProgress } from "../../lib/prepChecklistStorage";

type Props = {
  eventId: number;
  items: PrepChecklistItem[];
};

export function EventPrepChecklist({ eventId, items }: Props) {
  const { copy } = useSiteCopy();
  const [checked, setChecked] = useState<string[]>(() => loadPrepProgress(eventId));

  const progress = useMemo(() => {
    if (!items.length) return 0;
    return Math.round((checked.length / items.length) * 100);
  }, [checked.length, items.length]);

  function toggle(id: string) {
    setChecked((prev) => {
      const next = prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id];
      savePrepProgress(eventId, next);
      return next;
    });
  }

  if (!items.length) return null;

  return (
    <div className="participation-prep-checklist">
      <div className="participation-prep-head">
        <span className="participation-prep-ring" style={{ "--prep-pct": `${progress}%` } as React.CSSProperties}>
          {checked.length}/{items.length}
        </span>
        <span>{String(getAtPath(copy, "home.participation.prepProgress") ?? "ready for the night")}</span>
      </div>
      <ul className="participation-prep-list">
        {items.map((item) => {
          const isChecked = checked.includes(item.id);
          return (
            <li key={item.id}>
              <button
                type="button"
                className={`participation-prep-item${isChecked ? " is-checked" : ""}`}
                onClick={() => toggle(item.id)}
              >
                <span className="participation-prep-box" aria-hidden="true" />
                {item.href ? (
                  <Link to={item.href} onClick={(e) => e.stopPropagation()}>
                    {item.label}
                  </Link>
                ) : (
                  <span>{item.label}</span>
                )}
              </button>
            </li>
          );
        })}
      </ul>
    </div>
  );
}
